import {client} from "@/httpClient";
import {locationAtom} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import {captureErrorInSentry} from "@/utils/errorHandlers";
import "@tanstack/react-query";
import {MutationCache, QueryCache, QueryClient, type Mutation, type QueryFunction} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import i18next from "i18next";
import {getDefaultStore} from "jotai";
import {toast} from "react-toastify";

declare module "@tanstack/react-query" {
  interface Register {
    defaultError: AxiosError;
    mutationMeta: {
      errorMessage?: string;
      successMessage?: string;
    };
  }
}

const ROUTES_WITHOUT_UNAUTHORIZED_REDIRECT = ["assets", "management", "management_stats"] as const;

const EXCLUDE_TOAST_FOR_MUTATION_KEYS = [
  "/agency/\\d+/add_sub_brand/",
  "/agency/merge_brands/",
  "/agency/\\d+/register_agency_user/",
  "/buyout_bundles/one_time_payment/",
  "/campaign/\\d+/bulk_invite/",
  "/campaign/\\d+/start_campaign/",
  "/campaign_application/\\d+/cancel/",
  "/company/",
  "/group/\\d+/add_invite_influencers/",
  "/group/\\d+/remove_invite_influencers/",
  "/harvest/scraping/",
  "/invite_user/",
  "/notifications/seen/",
  "/sub_brand/\\d+/register_sub_brand_user/",
  "/token/",
  "/users/",

  // Additional (2nd) parameters for mutationKey for toast exclusion
  "brand_picture",
] as const;

const EXCLUDE_TOAST_FOR_SUCCESS_MUTATION_KEYS = [
  ...EXCLUDE_TOAST_FOR_MUTATION_KEYS,
  "/campaign/\\d+/assets/",
  "/campaign/\\d+/switch/",
  "/campaign/\\d+/duplicate/",
  "/campaign_application/like_media/",
  "/campaign_invite/",
  "/chat/message/",
  "/chat/upload_image/",
  "/copy_video_code/",
  "/group/\\d+/add_influencer/",
  "/group/\\d+/remove_influencer/",
  "/package_subscriptions/cancel/",
  "/remove_campaign_invite/",
  "/restructure-field/",
  "/sub_brand/\\d+/respond_permission_upgrade/",

  // Additional (2nd) parameters for mutationKey for toast exclusion
  "toggle_featured",
  "update_interests",
] as const;

const defaultQueryFn: QueryFunction = async ({queryKey, pageParam}) => {
  if (!!pageParam) {
    return await client.get(
      (Number(pageParam) ? `${queryKey[0]}?page=${pageParam}` + ((queryKey[1] as string) ?? "") : pageParam) as string,
    );
  } else {
    return await client.get(queryKey[0] as string);
  }
};

const mutationCache = new MutationCache({
  onError(error) {
    const mutation: Mutation = arguments[3];
    if (
      EXCLUDE_TOAST_FOR_MUTATION_KEYS.some(key =>
        mutation.options.mutationKey?.some(k => new RegExp(key).test(k as string)),
      )
    ) {
      return;
    }
    toast((mutation.options.meta?.errorMessage as string) ?? i18next.t("toastGlobalError"), {
      type: "error",
    });
    captureErrorInSentry(
      error as AxiosError<{detail?: string | string[]; error?: string | string[]; picture?: string[]}>,
    );
  },
  onSuccess() {
    const mutation: Mutation = arguments[3];
    if (
      EXCLUDE_TOAST_FOR_SUCCESS_MUTATION_KEYS.some(key =>
        mutation.options.mutationKey?.some(k => new RegExp(key).test(k as string)),
      )
    ) {
      return;
    }
    toast((mutation.options.meta?.successMessage as string) ?? i18next.t("toastGlobalSuccess"), {
      type: "success",
    });
  },
});

const queryCache = new QueryCache({
  async onError(error: AxiosError) {
    if (
      error.response?.status === 401 &&
      !ROUTES_WITHOUT_UNAUTHORIZED_REDIRECT.some(route => error.config?.url?.includes(route))
    ) {
      await useTokenStore.getState().logout();
      getDefaultStore().set(locationAtom, {
        previousRoute: getDefaultStore().get(locationAtom).currentRoute ?? "",
        currentRoute: (window.location.pathname ?? "") + (location.search ?? ""),
      });
    }
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      queryFn: defaultQueryFn,
      refetchOnMount: false,
      retry: false,
    },
    mutations: {},
  },
});

export {queryCache, queryClient};
